import * as React from "react";

import { useMemo, useContext, useState } from "react";
import { useTheme } from "@mui/material";
import { translation } from "../Dictionary";
import { AppContext } from "../../contextProviders/AppContext";
import {
  differenceInMinutes,
  set,
  startOfDay,
  getDay,
  addHours,
  isToday,
  isSameDay,
  addDays,
  add,
  roundToNearestMinutes,
  isBefore,
} from "date-fns";
import DatePickerMobile from "./DatePickerMobile";
import DatePickerDesktop from "./DatePickerDesktop";
import DatePickerFixedTimeframe from "./DateSingleDay";
import DatePickerFixedDuration from "./DateFixedDuration";

const throttle = (callback, limit) => {
  let wait = false;
  return (...args) => {
    if (!wait) {
      wait = true;
      setTimeout(function () {
        wait = false;
      }, limit);
      return callback(...args);
    }
  };
};

const SLIDER_STEP = 30;

export default function SelectDate({ bgColor, contentHeigth }) {
  const {
    mobileViewPort,
    selectedDateRange,
    setSelectedDateRange,
    selectedCompany,
    reservationStart,
    reservationReturn,
    selectedPicker,
    setSelectedPicker,
    disableMultiday,
    businessHourDates,
    startDateString,
    hasError,
    dateRange,
    endTimeOk,
    VUOKRAAPAKU_SPECIAL_COMPANY,
    endDateString,
    FEATURE_FIXED_RESERVATION_TIMEFRAME,
    FEATURE_FIXED_RESERVATION_DURATION,
    FEATURE_NO_TIME_SELECT,
    useSpecificPickupReturnCompany,
    groups,
    language,
  } = useContext(AppContext);

  const startTime = selectedDateRange[0];
  const endTime = selectedDateRange[1];

  const startTimeLabel = useMemo(() => {
    const group = groups?.[0];
    if (group === "gnista") {
      return translation.get(language, "Timepicker start title accommodation");
    } else {
      return translation.get(language, "Timepicker start title");
    }
  }, [groups, language]);

  const returnTimeLabel = useMemo(() => {
    const group = groups?.[0];
    if (group === "gnista") {
      return translation.get(language, "Timepicker return title accommodation");
    } else {
      return translation.get(language, "Timepicker return title");
    }
  }, [groups, language]);

  // This feature disables selecting start & return time and gives them fixed values
  const FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA = useMemo(() => {
    if (!selectedCompany) return;
    if (selectedCompany.id === "c2da4105-2bd5-488c-ae7d-ffa50e048356") {
      return true;
    }
    return false;
  }, [selectedCompany]);

  const setStartTime = (value) => {
    setSelectedDateRange((prevState) => [value, prevState[1]]);
  };

  const setEndTime = (value) => {
    setSelectedDateRange((prevState) => [prevState[0], value]);
  };

  const theme = useTheme();
  const [openDateRangeMobile, setOpenDateRangeMobile] = useState(false);

  const disableSameDay = useMemo(() => {
    if (selectedCompany?.disableSameDay) return selectedCompany;
    return null;
  }, [selectedCompany]);

  function handleSelectedTimeFrame(time, indexValue) {
    if (!startTime) return console.log("fail");
    setSelectedTimeFrame(fixedTimeFrames[indexValue]);
    // Define endtime
    return setEndTime(addHours(startTime, time));
  }

  // TODO: Will be read from selectedCompany

  const fixedTimeFrames = [4, 8, 24];
  const defaultTimeFrame = useMemo(() => {
    return fixedTimeFrames[0];
  }, [selectedCompany]);

  // DateRangePicker has a date value that needs to be converted to support the old version "dateRange".
  const handleDateChange = (newValue) => {
    // Set fixed time for start and end dates.
    // When this feature is enabled, customer doesnt have access to change times
    if (FEATURE_NO_TIME_SELECT) {
      // incase the selected dates are same day, change the hours accordingly
      if (isSameDay(newValue[0], newValue[1])) {
        const start = set(newValue[0], {
          hours: 0,
          minutes: 0,
        });

        const end = set(newValue[1], { hours: 23, minutes: 59 });
        return setSelectedDateRange([start, end]);
      } else {
        const start = set(newValue[0], {
          hours: 23,
          minutes: 59,
        });

        const end = set(newValue[1], { hours: 0, minutes: 0 });
        return setSelectedDateRange([start, end]);
      }
    }

    if (disableMultiday) {
      // newValue = date instead of an array [date, date],
      //because DatePicker is used instead of DaterangePicker

      if (!reservationStart) {
        if (businessHourDates([newValue, newValue], selectedCompany)) {
          const timeFrame = businessHourDates(
            [newValue, newValue],
            selectedCompany
          );

          // if closing time happens to be 00:00, this will produce an error,
          // so instead set the endTime to +1 hours from start
          const closingTime =
            timeFrame.end.closingTime.getTime() > new Date().getTime()
              ? timeFrame.end.closingTime
              : set(new Date(), {
                  hours: new Date().getHours() + 1,
                  minutes: new Date().getMinutes(),
                });

          // if selected start date is the current day, check for the time settings
          if (isToday(newValue)) {
            if (timeFrame.start.openingTime.getTime() > new Date().getTime()) {
              setSelectedDateRange([
                timeFrame.start.openingTime,
                addHours(timeFrame.start.openingTime, selectedTimeFrame),
              ]);
            } else {
              // this prevents setting the startTime to past
              setSelectedDateRange([
                new Date(),
                // set the end time here,
                addHours(new Date(), selectedTimeFrame),
                ,
              ]);
            }
          } else {
            const newEndTime = addHours(
              timeFrame.start.openingTime,
              selectedTimeFrame
            );

            setStartTime(timeFrame.start.openingTime);
            setEndTime(newEndTime);
            setSelectedDateRange([timeFrame.start.openingTime, newEndTime]);
          }
        } else {
          // no businessHourDates(), time values are 00:00
          // when changing date from the datepicker, add the fixed endtime to prevent errors
          //  setDateRange({ from: date, to: date });
          setStartTime(newValue);
          setEndTime(addHours(newValue, selectedTimeFrame));
          setSelectedDateRange([newValue, newValue]);
        }
      } else {
        setSelectedDateRange([
          set(newValue, {
            hours: reservationStart.getHours() + 1,
            minutes: reservationStart.getMinutes(),
          }),
          set(newValue, {
            hours: reservationReturn.getHours() + 1,
            minutes: reservationReturn.getMinutes(),
          }),
        ]);
        setStartTime(
          set(newValue, {
            hours: reservationStart.getHours() + 1,
            minutes: reservationStart.getMinutes(),
          })
        );
        setEndTime(
          set(newValue, {
            hours: reservationReturn.getHours() + 1,
            minutes: reservationReturn.getMinutes(),
          })
        );
      }
      if (FEATURE_FIXED_RESERVATION_DURATION) {
        const duration = 4;
        const endDate = new Date(
          add(newValue[0], { days: duration }).setHours(12, 0, 0)
        );
        const startDate = new Date(newValue[0].setHours(12, 0, 0));

        setSelectedDateRange([startDate, endDate]);
      }
    }

    if (!disableMultiday) {
      // newValue = [date, date], because of DaterangePicker
      // if no previous search, set the "startTime" to opening hour of the day
      if (!reservationStart && !FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA) {
        if (businessHourDates(newValue, selectedCompany)) {
          const timeFrame = businessHourDates(newValue, selectedCompany);

          // if closing time happens to be 00:00, this will produce an error,
          // so instead set the endTime to +1 hours from start
          const closingTime =
            timeFrame.end.closingTime.getTime() > new Date().getTime()
              ? timeFrame.end.closingTime
              : set(new Date(), {
                  hours: new Date().getHours() + 1,
                  minutes: new Date().getMinutes(),
                });
          const addDeliveryBufferHours = selectedCompany?.deliveryBufferHours;
          // if selected start date is the current day, check for the time settings
          if (isToday(newValue[0])) {
            if (timeFrame.start.openingTime.getTime() > new Date().getTime()) {
              const start = addDeliveryBufferHours
                ? roundToNearestMinutes(
                    addHours(
                      timeFrame.start.openingTime,
                      addDeliveryBufferHours
                    ),
                    { nearestTo: 15 }
                  )
                : timeFrame.start.openingTime;
              const end = isBefore(closingTime, start)
                ? addHours(start, 1)
                : closingTime;
              setSelectedDateRange([start, end]);
            } else {
              const start = addDeliveryBufferHours
                ? roundToNearestMinutes(
                    addHours(new Date(), addDeliveryBufferHours),
                    { nearestTo: 15 }
                  )
                : new Date();
              const end = isBefore(closingTime, start)
                ? addHours(start, 1)
                : closingTime;
              // this prevents setting the startTime to past
              setSelectedDateRange([
                start,
                // set the end time here,
                end,
              ]);
            }
          } else {
            setSelectedDateRange([
              timeFrame.start.openingTime,
              timeFrame.end.closingTime,
            ]);
          }
        } else {
          // no businessHourDates(), time values are 00:00
          setSelectedDateRange(newValue);
        }
      }

      // if previous search exists, set the selected timeframe for the new dates
      if (reservationStart && !FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA) {
        setSelectedDateRange([
          set(newValue[0], {
            hours: reservationStart.getHours(),
            minutes: reservationStart.getMinutes(),
          }),
          set(newValue[1], {
            hours: reservationReturn.getHours(),
            minutes: reservationReturn.getMinutes(),
          }),
        ]);
      }
      if (FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA) {
        if (isSameDay(newValue[0], newValue[1])) {
          setSelectedDateRange([
            set(newValue[0], {
              hours: 15,
              minutes: 0,
            }),
            addDays(
              set(newValue[1], {
                hours: 12,
                minutes: 0,
              }),
              1
            ),
          ]);
        } else {
          setSelectedDateRange([
            set(newValue[0], {
              hours: 15,
              minutes: 0,
            }),
            set(newValue[1], {
              hours: 12,
              minutes: 0,
            }),
          ]);
        }
      }
    }
  };

  // TimePicker control
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(defaultTimeFrame);
  const dayStart = startOfDay(new Date());

  const getSpecificFromToTimes = (date) => {
    if (!useSpecificPickupReturnCompany) return null;
    return useSpecificPickupReturnCompany.weeklyPickupReturnTimes[getDay(date)];
  };

  const startSliderValue = useMemo(
    () => differenceInMinutes(startTime, startOfDay(startTime)) / SLIDER_STEP,
    [startTime]
  );

  const endSliderValue = useMemo(
    () => differenceInMinutes(endTime, startOfDay(endTime)) / SLIDER_STEP,
    [endTime]
  );

  // ID of Vuokraapaku Company Sörnäinen
  const startEndSameDay = useMemo(() => {
    if (!dateRange?.to || dateRange?.from?.day === dateRange?.to?.day)
      return true;
    return false;
  }, [dateRange]);

  const disableStartDaysFromNow = useMemo(() => {
    if (selectedCompany?.disableStartDaysFromNow)
      return selectedCompany.disableStartDaysFromNow;
    return null;
  }, [selectedCompany]);
  const enableVehicleStartReturnTimes = useMemo(() => {
    if (selectedCompany?.enableVehicleStartReturnTimes) return selectedCompany;
    return null;
  }, [selectedCompany]);

  const mobileViewStyles = {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "lightgrey",
    borderRadius: "25px",
    padding: "5px",
    marginBottom: "25px",
    boxShadow: theme.shadows[0],
    bgcolor: bgColor,
  };

  const getMinimumDate = () => {
    let date = new Date();
    if (disableSameDay) {
      // Get tomorrow
      date = addDays(date, 1);
    }
    if (disableStartDaysFromNow) {
      date = addDays(date, disableStartDaysFromNow);
    }

    return date;
  };

  // Default mobile reservation schedule
  if (mobileViewPort) {
    return (
      <DatePickerMobile
        startTimeLabel={startTimeLabel}
        returnTimeLabel={returnTimeLabel}
        dateRange={dateRange}
        startDateString={startDateString}
        endDateString={endDateString}
        VUOKRAAPAKU_SPECIAL_COMPANY={VUOKRAAPAKU_SPECIAL_COMPANY}
        selectedCompany={selectedCompany}
        setSelectedTimeFrame={setSelectedTimeFrame}
        hasError={hasError}
        endTimeOk={endTimeOk}
        fixedTimeFrames={fixedTimeFrames}
        startSliderValue={startSliderValue}
        endSliderValue={endSliderValue}
        startEndSameDay={startEndSameDay}
        dayStart={dayStart}
        selectedDateRange={selectedDateRange}
        handleDateChange={handleDateChange}
        setOpenDateRangeMobile={setOpenDateRangeMobile}
        startTime={startTime}
        endTime={endTime}
        SLIDER_STEP={SLIDER_STEP}
        disableSameDay={disableSameDay}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        selectedTimeFrame={selectedTimeFrame}
        throttle={throttle}
        openDateRangeMobile={openDateRangeMobile}
        FEATURE_FIXED_RESERVATION_TIMEFRAME={
          FEATURE_FIXED_RESERVATION_TIMEFRAME
        }
        FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA={
          FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA
        }
        getMinimumDate={getMinimumDate}
      />
    );
  }

  // Default desktop reservation schedule
  if (!disableMultiday && !mobileViewPort) {
    return (
      <DatePickerDesktop
        startTimeLabel={startTimeLabel}
        returnTimeLabel={returnTimeLabel}
        contentHeigth={contentHeigth}
        fixedTimeFrames={fixedTimeFrames}
        startSliderValue={startSliderValue}
        endSliderValue={endSliderValue}
        handleDateChange={handleDateChange}
        setOpenDateRangeMobile={setOpenDateRangeMobile}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        dateRange={dateRange}
        startDateString={startDateString}
        endDateString={endDateString}
        selectedCompany={selectedCompany}
        setSelectedTimeFrame={setSelectedTimeFrame}
        hasError={hasError}
        endTimeOk={endTimeOk}
        startEndSameDay={startEndSameDay}
        dayStart={dayStart}
        selectedDateRange={selectedDateRange}
        startTime={startTime}
        endTime={endTime}
        SLIDER_STEP={SLIDER_STEP}
        disableSameDay={disableSameDay}
        selectedTimeFrame={selectedTimeFrame}
        throttle={throttle}
        openDateRangeMobile={openDateRangeMobile}
        FEATURE_FIXED_RESERVATION_TIMEFRAME={
          FEATURE_FIXED_RESERVATION_TIMEFRAME
        }
        FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA={
          FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA
        }
        FEATURE_NO_TIME_SELECT={FEATURE_NO_TIME_SELECT}
        getMinimumDate={getMinimumDate}
      />
    );
  }

  // Single date desktop reservation schedule
  if (disableMultiday && !mobileViewPort) {
    if (FEATURE_FIXED_RESERVATION_DURATION) {
      return (
        <DatePickerFixedDuration
          startTimeLabel={startTimeLabel}
          returnTimeLabel={returnTimeLabel}
          contentHeigth={contentHeigth}
          dateRange={dateRange}
          disableMultiday={disableMultiday}
          handleSelectedTimeFrame={handleSelectedTimeFrame}
          defaultTimeFrame={defaultTimeFrame}
          startDateString={startDateString}
          endDateString={endDateString}
          VUOKRAAPAKU_SPECIAL_COMPANY={VUOKRAAPAKU_SPECIAL_COMPANY}
          selectedCompany={selectedCompany}
          setSelectedTimeFrame={setSelectedTimeFrame}
          hasError={hasError}
          endTimeOk={endTimeOk}
          fixedTimeFrames={fixedTimeFrames}
          startSliderValue={startSliderValue}
          endSliderValue={endSliderValue}
          startEndSameDay={startEndSameDay}
          dayStart={dayStart}
          selectedDateRange={selectedDateRange}
          handleDateChange={handleDateChange}
          setOpenDateRangeMobile={setOpenDateRangeMobile}
          startTime={startTime}
          endTime={endTime}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          SLIDER_STEP={SLIDER_STEP}
          disableSameDay={disableSameDay}
          selectedTimeFrame={selectedTimeFrame}
          throttle={throttle}
          openDateRangeMobile={openDateRangeMobile}
          FEATURE_FIXED_RESERVATION_TIMEFRAME={
            FEATURE_FIXED_RESERVATION_TIMEFRAME
          }
          selectedPicker={selectedPicker}
          setSelectedPicker={setSelectedPicker}
          setSelectedDateRange={setSelectedDateRange}
          getMinimumDate={getMinimumDate}
        />
      );
    }

    return (
      <DatePickerFixedTimeframe
        startTimeLabel={startTimeLabel}
        returnTimeLabel={returnTimeLabel}
        contentHeigth={contentHeigth}
        dateRange={dateRange}
        disableMultiday={disableMultiday}
        handleSelectedTimeFrame={handleSelectedTimeFrame}
        defaultTimeFrame={defaultTimeFrame}
        startDateString={startDateString}
        endDateString={endDateString}
        VUOKRAAPAKU_SPECIAL_COMPANY={VUOKRAAPAKU_SPECIAL_COMPANY}
        selectedCompany={selectedCompany}
        setSelectedTimeFrame={setSelectedTimeFrame}
        hasError={hasError}
        endTimeOk={endTimeOk}
        fixedTimeFrames={fixedTimeFrames}
        startSliderValue={startSliderValue}
        endSliderValue={endSliderValue}
        startEndSameDay={startEndSameDay}
        dayStart={dayStart}
        selectedDateRange={selectedDateRange}
        handleDateChange={handleDateChange}
        setOpenDateRangeMobile={setOpenDateRangeMobile}
        startTime={startTime}
        endTime={endTime}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        SLIDER_STEP={SLIDER_STEP}
        disableSameDay={disableSameDay}
        selectedTimeFrame={selectedTimeFrame}
        throttle={throttle}
        openDateRangeMobile={openDateRangeMobile}
        FEATURE_FIXED_RESERVATION_TIMEFRAME={
          FEATURE_FIXED_RESERVATION_TIMEFRAME
        }
        selectedPicker={selectedPicker}
        setSelectedPicker={setSelectedPicker}
        setSelectedDateRange={setSelectedDateRange}
        getMinimumDate={getMinimumDate}
      />
    );
  }
}
